// Ultima actividad de un caso y si la persona ya la vio (D42): en Mi dia, Mis casos y Todos se ve "hace 5 min ·
// cliente" y, si hay algo nuevo desde la ultima vez que abrio el caso, se marca como "sin ver". Se guarda en este
// navegador, por persona; la primera vez todo cuenta como visto (no se llena la pantalla de avisos viejos).
import { useSyncExternalStore } from 'react';

import { type Caso } from '../api/casos';
import { readJSON, writeJSON } from './storage';

export type Vistos = { desde: string; casos: Record<string, string> };
export type Actividad = { fecha: string | null; quien: 'cliente' | 'equipo' | null };

const max = (xs: (string | null | undefined)[]) => xs.filter((x): x is string => !!x && !Number.isNaN(Date.parse(x))).sort((a, b) => Date.parse(b) - Date.parse(a))[0] ?? null;

// Lo ultimo que paso en el caso y quien lo hizo.
export function actividadDe(c: Pick<Caso, 'ultimoCliente' | 'ultimoEquipo' | 'ultimoMensaje' | 'creado'>): Actividad {
  const fecha = max([c.ultimoCliente, c.ultimoEquipo, c.ultimoMensaje]) ?? c.creado ?? null;
  if (!fecha) return { fecha: null, quien: null };
  const t = Date.parse(fecha);
  const quien = c.ultimoCliente && Date.parse(c.ultimoCliente) >= t ? 'cliente' : c.ultimoEquipo && Date.parse(c.ultimoEquipo) >= t ? 'equipo' : null;
  return { fecha, quien };
}

// Hay algo nuevo desde la ultima vez que la persona abrio el caso (o desde que empezo a usar los avisos).
export function sinVer(c: Pick<Caso, 'id' | 'ultimoCliente' | 'ultimoEquipo' | 'ultimoMensaje' | 'creado'>, v: Vistos) {
  const a = actividadDe(c);
  if (!a.fecha) return false;
  return Date.parse(a.fecha) > Date.parse(v.casos[c.id] ?? v.desde);
}

/* ---------------- almacen por persona ---------------- */
const clave = (miembroId: string) => `consola.casos.vistos.${miembroId}`;
let dueno = '';
let vistos: Vistos = { desde: new Date().toISOString(), casos: {} };
const subs = new Set<() => void>();

function cargar(miembroId: string) {
  if (dueno === miembroId) return;
  dueno = miembroId;
  const v = readJSON<Vistos | null>(clave(miembroId), null);
  vistos = v && typeof v.desde === 'string' ? { desde: v.desde, casos: v.casos ?? {} } : { desde: new Date().toISOString(), casos: {} };
  if (!v) writeJSON(clave(miembroId), vistos);
}

export function marcarVisto(miembroId: string | null | undefined, casoId: string, fecha: string | null) {
  if (!miembroId || !fecha) return;
  cargar(miembroId);
  const previo = vistos.casos[casoId];
  if (previo && Date.parse(previo) >= Date.parse(fecha)) return;
  // Se guardan los 500 mas recientes.
  const casos = Object.fromEntries(
    Object.entries({ ...vistos.casos, [casoId]: fecha })
      .sort((a, b) => Date.parse(b[1]) - Date.parse(a[1]))
      .slice(0, 500),
  );
  vistos = { ...vistos, casos };
  writeJSON(clave(miembroId), vistos);
  subs.forEach((s) => s());
}

export function useVistos(miembroId: string | null | undefined) {
  if (miembroId) cargar(miembroId);
  return useSyncExternalStore(
    (cb) => {
      subs.add(cb);
      return () => subs.delete(cb);
    },
    () => vistos,
  );
}
